import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2, BookOpen, Search, CheckCircle, Clock, CalendarCheck, XCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
// iter207 · Embebemos la UI de "Reservar Espacio" como pestaña dentro
// de la Biblioteca "Laura Iglesias" (los matriculadxs llegan por la card
// principal de la landing).
import ReservarEspacioPage from '../reservas/ReservarEspacioPage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BibliotecaPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('buscar'); // buscar | mis | espacio
  const [q, setQ] = useState('');
  const [tema, setTema] = useState('');
  const [temas, setTemas] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-indexed
  const [loading, setLoading] = useState(false);
  const [mis, setMis] = useState([]);
  const [reservando, setReservando] = useState(null);
  const [espacioInfo, setEspacioInfo] = useState(null);

  const PAGE_SIZE = 50;

  const buscar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/biblioteca/titulos`, {
        params: { q, tema, limit: PAGE_SIZE, skip: page * PAGE_SIZE },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error('Error buscando'); }
    setLoading(false);
  }, [q, tema, page]);

  const loadMis = useCallback(async () => {
    try { const r = await axios.get(`${API}/biblioteca/mis-prestamos`); setMis(r.data || []); }
    catch (e) { console.error(e); }
  }, []);

  const loadTemas = useCallback(async () => {
    try { const r = await axios.get(`${API}/biblioteca/temas`); setTemas(r.data.temas || []); }
    catch (e) { /* silencioso */ }
  }, []);

  const loadEspacioInfo = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/reservas-espacio/info`);
      setEspacioInfo(r.data || null);
    } catch (e) { /* silencioso — si no está configurado, no mostramos banner */ }
  }, []);

  // Reset page cuando cambian búsqueda o tema (sino quedás en page>0 con 0 resultados).
  useEffect(() => { setPage(0); }, [q, tema]);
  useEffect(() => { buscar(); }, [buscar]);
  useEffect(() => { loadMis(); loadTemas(); loadEspacioInfo(); }, [loadMis, loadTemas, loadEspacioInfo]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const reservar = async (titulo) => {
    if (!window.confirm(`¿Reservar "${titulo.titulo}"? Tenés 3 días para retirarlo tras la confirmación.`)) return;
    setReservando(titulo.id);
    try {
      const r = await axios.post(`${API}/biblioteca/reservar/${titulo.id}`);
      toast.success('Reserva confirmada', {
        description: `Te enviamos un mail con los detalles. Tenés ${r.data.fecha_limite_retiro_dias} días para retirar.`
      });
      buscar();
      loadMis();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error reservando');
    }
    setReservando(null);
  };

  const estadoColor = (e) => ({
    reservado: 'bg-amber-100 text-amber-700',
    activo: 'bg-blue-100 text-blue-700',
    devuelto: 'bg-emerald-100 text-emerald-700',
    vencido: 'bg-red-100 text-red-700',
    expirado: 'bg-gray-100 text-gray-500',
    cancelado: 'bg-gray-100 text-gray-500',
  }[e] || 'bg-gray-100');

  const [cancelando, setCancelando] = useState(null);
  const cancelarReserva = async (p) => {
    if (!window.confirm(`¿Cancelar la reserva de "${p.titulo?.titulo || 'este libro'}"? El ejemplar volverá a estar disponible para otras personas.`)) return;
    setCancelando(p.id);
    try {
      await axios.post(`${API}/biblioteca/reservas/${p.id}/cancelar`);
      toast.success('Reserva cancelada');
      buscar();
      loadMis();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cancelar');
    }
    setCancelando(null);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-5" data-testid="biblioteca-page">
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-amber-700 transition-colors"
        data-testid="btn-volver-inicio-biblioteca"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver al inicio
      </button>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-amber-600" /> Biblioteca "Laura Iglesias"
        </h1>
        <p className="text-sm text-gray-600">
          Explorá el catálogo disponible, reservá ejemplares para retirar en la sede y pedí turno para usar el espacio del Colegio.
        </p>
      </div>

      {/* Banner unificado — mismo header que trae `reservas-espacio/info`, visible en las 3 pestañas */}
      {espacioInfo?.header_imagen_url && (
        <div
          className="w-full rounded-xl overflow-hidden bg-gray-200 shadow-sm"
          style={{ aspectRatio: '3 / 1' }}
          data-testid="biblioteca-header-banner"
        >
          <img
            src={espacioInfo.header_imagen_url}
            alt="Biblioteca Laura Iglesias"
            className="w-full h-full object-cover"
            style={{
              objectPosition: `${espacioInfo?.header_position_x ?? 50}% ${espacioInfo?.header_position_y ?? 50}%`,
            }}
          />
        </div>
      )}

      <div className="flex gap-1 border-b overflow-x-auto">
        <button onClick={() => setTab('buscar')}
          className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-2 ${tab === 'buscar' ? 'border-amber-600 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-buscar">
          <Search className="w-4 h-4" /> Catálogo
        </button>
        <button onClick={() => setTab('mis')}
          className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-2 ${tab === 'mis' ? 'border-amber-600 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-mis-prestamos">
          <BookOpen className="w-4 h-4" /> Mis préstamos
          {mis.length > 0 && <Badge className="bg-amber-100 text-amber-800 ml-1">{mis.length}</Badge>}
        </button>
        <button onClick={() => setTab('espacio')}
          className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-2 ${tab === 'espacio' ? 'border-amber-600 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-reservar-espacio">
          <CalendarCheck className="w-4 h-4" /> Reservar espacio
        </button>
      </div>

      {tab === 'buscar' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Search className="w-4 h-4 text-gray-500 shrink-0" />
              <Input placeholder="Buscar por título, autor, editorial o tema..."
                value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && buscar()}
                className="max-w-md" data-testid="input-buscar" />
              <Button size="sm" onClick={buscar} data-testid="btn-buscar">Buscar</Button>
              {!loading && total > 0 && (
                <span className="text-xs text-gray-500 ml-2" data-testid="contador-resultados">
                  Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-3">
              <label className="text-xs text-gray-600 shrink-0">Filtrar por tema:</label>
              <select
                className="border rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                value={tema}
                onChange={e => setTema(e.target.value)}
                data-testid="select-tema-filter"
              >
                <option value="">Todos los temas</option>
                {temas.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {tema && (
                <button
                  onClick={() => setTema('')}
                  className="text-xs text-amber-700 hover:underline"
                  data-testid="btn-clear-tema"
                >
                  Limpiar filtro
                </button>
              )}
            </div>
            {!q && !tema && (
              <p className="text-xs text-gray-500 mt-2">
                Dejá el buscador vacío para explorar todo el catálogo o escribí/filtrá por tema.
              </p>
            )}
          </CardHeader>
          <CardContent>
            {!loading && total > PAGE_SIZE && (
              <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b" data-testid="pagination-controls-top">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  data-testid="btn-prev-page-top"
                >
                  ← Anterior
                </Button>
                <span className="text-xs text-gray-600">
                  Página {page + 1} de {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="btn-next-page-top"
                >
                  Siguiente →
                </Button>
              </div>
            )}
            {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-8 text-amber-600" /> : (
              items.length === 0 ? <p className="text-center text-gray-500 py-8">Sin resultados</p> : (
                <div className="space-y-3">
                  {items.map(t => (
                    <div key={t.id} className="border rounded-lg p-4 flex items-start justify-between gap-4 hover:bg-gray-50" data-testid={`card-titulo-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{t.titulo}</p>
                        <p className="text-sm text-gray-600">{t.autor || 'Sin autor'} · {t.editorial || 'Sin editorial'}</p>
                        {t.tema && <p className="text-xs text-gray-500 mt-1">Tema: {t.tema}</p>}
                      </div>
                      <div className="text-right space-y-1 shrink-0">
                        <Badge className={t.ejemplares_disponibles > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
                          {t.ejemplares_disponibles} disponible{t.ejemplares_disponibles !== 1 ? 's' : ''}
                        </Badge>
                        <div>
                          <Button size="sm" disabled={t.ejemplares_disponibles === 0 || reservando === t.id} onClick={() => reservar(t)} data-testid={`btn-reservar-${t.id}`}>
                            {reservando === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reservar'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
            {!loading && total > PAGE_SIZE && (
              <div className="flex items-center justify-between gap-2 mt-5 pt-4 border-t" data-testid="pagination-controls">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  data-testid="btn-prev-page"
                >
                  ← Anterior
                </Button>
                <span className="text-xs text-gray-600" data-testid="page-indicator">
                  Página {page + 1} de {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="btn-next-page"
                >
                  Siguiente →
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'mis' && (
        <Card>
          <CardHeader><CardTitle className="text-base">Mis préstamos y reservas</CardTitle></CardHeader>
          <CardContent>
            {mis.length === 0 ? <p className="text-center text-gray-500 py-8">Todavía no reservaste ningún libro.</p> : (
              <div className="space-y-2">
                {mis.map(p => (
                  <div key={p.id} className="border rounded p-3 flex items-start justify-between gap-3" data-testid={`row-mi-prestamo-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{p.titulo?.titulo || '-'}</p>
                      <p className="text-xs text-gray-600">{p.titulo?.autor} · {p.titulo?.editorial}</p>
                      {p.estado === 'reservado' && p.fecha_limite_retiro && (
                        <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Retirá antes del {new Date(p.fecha_limite_retiro).toLocaleDateString('es-AR')}
                        </p>
                      )}
                      {p.fecha_vencimiento && p.estado === 'activo' && (
                        <p className="text-xs text-blue-700 mt-1">Devolver antes del {new Date(p.fecha_vencimiento).toLocaleDateString('es-AR')}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <Badge className={estadoColor(p.estado)}>{p.estado}</Badge>
                      {p.estado === 'reservado' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-700 border-red-200 hover:bg-red-50"
                          disabled={cancelando === p.id}
                          onClick={() => cancelarReserva(p)}
                          data-testid={`btn-cancelar-reserva-${p.id}`}
                        >
                          {cancelando === p.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <><XCircle className="w-3 h-3 mr-1" /> Cancelar reserva</>}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'espacio' && (
        <div data-testid="tab-content-espacio">
          <ReservarEspacioPage embedded />
        </div>
      )}
    </div>
  );
}
